import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import update from 'lodash/fp/update';
import filter from 'lodash/fp/filter';
import classes from '@nerdwallet/base-styles/classes';
import classNames from 'classnames';
import { Title, Text, Box } from '@nerdwallet/currency';

import {
  stringInputSpec,
  stringTextAreaSpec,
  stringSelectSpec,
  dataDependencyTypePicker,
  dataDependencyIdsAdder,
  generateEditingConfig,
  anchorIdInput,
} from '../../lib/editing-configs';
import getDefaultCtaFromDependency from '../../lib/product-table-utils';
import withDataDependency from '../../lib/with-data-dependency-wrapper';
import { unpackDataDependencyIds } from '../../lib/helpers';
// TODO: fix import cycle
// eslint-disable-next-line import/no-cycle
import { getSlug } from '~/server/config-fetcher';

import ExpandingProductTable from '../expanding-product-table/expanding-product-table';

import Row from '../row/row';

import './editable-product-table.less';

const tables = {
  investing: ExpandingProductTable,
  edu: ExpandingProductTable,
};

const dependenciesToTableTypes = {
  studentLoans: ['edu'],
  investing: ['investing'],
};

const getTableType = (type, dataDependencyType) => {
  const tableTypesForDependency = _.get(
    dependenciesToTableTypes,
    dataDependencyType
  );
  // if no data dependency is present or the table type is included in the list of tables for that data dep, use the given type
  // otherwise, use the table for the data dependency
  const tableType =
    !tableTypesForDependency || _.includes(tableTypesForDependency, type)
      ? type
      : tableTypesForDependency[0];
  return tableType;
};

const EditableProductTable = ({
  anchorId,
  dataDependency,
  title,
  subtitle,
  name,
  type = 'investing',
  requestedIds,
  products,
  ...otherTableProps // as defined by subconfigs, passed to table
}) => {
  const tableType = getTableType(type, dataDependency.type);
  const ChosenTable = tables[tableType];

  if (_.isEmpty(products)) {
    return null;
  }

  return (
    <Row centeredInParent dataIntegration="editable-product-table">
      <Box
        className={classNames([
          'landing-page-standard__max-widths',
          classes('margin-horizontal-auto'),
          classes('padding-bottom-4'),
        ])}
        id={anchorId}
      >
        {title && (
          <Title size="small" className="product-table__title">
            {title}
          </Title>
        )}
        {subtitle && (
          <Box className="product-table__subtitle">
            <Text align="center">{subtitle}</Text>
          </Box>
        )}
      </Box>
      <ChosenTable
        ids={filter(Boolean, requestedIds)}
        name={name}
        allProducts={products}
        type={tableType}
        getDefaultCta={getDefaultCtaFromDependency(dataDependency.type)}
        {...otherTableProps}
      />
    </Row>
  );
};

EditableProductTable.displayName = 'EditableProductTable';

EditableProductTable.propTypes = {
  anchorId: PropTypes.string,
  name: PropTypes.string,
  ctaCopy: PropTypes.string,
  dataDependency: PropTypes.shape({
    data: {
      ids: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string })),
    },
    type: PropTypes.string,
    productPage: PropTypes.string,
  }),
  title: PropTypes.string,
  subtitle: PropTypes.string,
  type: PropTypes.oneOf(Object.keys(tables)),
  // Provided by "withDataDependency"
  requestedIds: PropTypes.arrayOf(PropTypes.string),
  products: PropTypes.shape(PropTypes.object),
};

EditableProductTable.defaultProps = {
  data: {},
  dataDependency: {},
};

// only some data dependencies have tables, so we only want to offer data dependencies that map to tables as
// options in the editing config
const productDataDependencyPicker = update(
  'items',
  filter(({ value }) => value === '' || dependenciesToTableTypes[value]),
  dataDependencyTypePicker
);

EditableProductTable.getEditingConfig = (componentDocument) => {
  const dataDependency = _.get(componentDocument, 'dataDependency.type', '');
  // we want to only allow users to select table types that match the data dependency they've set
  const availableTableTypes = _.get(dependenciesToTableTypes, dataDependency);
  const tableTypes = availableTableTypes
    ? [{ value: undefined, label: 'Pick a table type' }].concat(
        availableTableTypes.map((tableName) => ({
          value: tableName,
          label: _.capitalize(tableName),
        }))
      )
    : [{ value: undefined, label: 'Pick a data dependency first' }];
  const getDefaultCta = getDefaultCtaFromDependency(dataDependency);

  return [
    stringInputSpec('name', 'Table name (for tracking)'),
    stringTextAreaSpec('title', 'Header'),
    stringTextAreaSpec('subtitle', 'Subheader'),
    productDataDependencyPicker,
    dataDependencyIdsAdder,

    stringSelectSpec('type', 'Table type', tableTypes, {
      help: 'Table type must match the type of product. Insert a new product table from the editing menu if you want to use a different type of product.',
    }),

    stringInputSpec('ctaCopy', 'CTA Button Copy', {
      placeholder: getDefaultCta(),
    }),
    // Surface the relevant table subconfig
    ...generateEditingConfig({
      Component:
        tables[
          getTableType(
            _.get(componentDocument, 'props.type', 'investing'),
            dataDependency
          )
        ],
    }),

    anchorIdInput,
  ];
};

/**
 * Map ingested props to data dependent fields by way of Structured-Content Provider data descriptions.
 * @see {@link https://github.com/NerdWallet/nwjs-structured-content-core#datadependencies}
 *
 * @param {Object} props
 * @param {Object} props.dataDependency - data dependency specifications
 * @param {Object} props.location - location info passed by way of `withRouter` HOC.
 */
/* istanbul ignore next */
const dataDependenciesWithRoute = ({ dataDependency, location }) => {
  const { type } = dataDependency;
  const { pathname = '' } = location;
  if (!type) {
    return null;
  }

  return {
    data: {
      productPage: getSlug(pathname),
      ids: unpackDataDependencyIds(dataDependency),
      type,
    },
  };
};

export default withDataDependency(
  EditableProductTable,
  dataDependenciesWithRoute
);
